import React from 'react';
import classNames from 'classnames';

import {useI18n} from '@udemy-v2/i18n';
import {BlockList} from '@udemy-v2/react-core-components';
import {Icon} from '@udemy-v2/icons';
import {TextSkeleton} from '@udemy-v2/react-reveal-components';
import {useMatchMedia} from '@udemy-v2/design-system-utils';
import {AsyncStaticPriceText, useCoursePrice} from '@udemy-v2/react-course-pricing';
import {CollapsiblePurchaseSection} from '@/features/course-landing-page/components/collapsible-purchase-section/collapsible-purchase-section.react-component';
import {
    PRICE_EVENTING_BUYABLE_TYPES,
    PriceEventTrackingProvider,
    PURCHASE_PRICE_TYPES,
} from '@udemy-v2/react-currency-formatting';
import {AddToCartButton, WishlistButton} from '@udemy-v2/react-shopping-buttons';

import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {TransactionalCTAButton} from '@/features/course-landing-page/components/transactional-cta-button/transactional-cta-button.react-component';
import {ClpCartSuccessModal} from '@/features/course-landing-page/components/clp-cart-success-modal';
import styles from './buy-box.module.scss';
import {
    CLPPurchaseOption,
    useClpPurchaseContext,
} from '../../context/clp-purchase-context.react-component';

interface CollapsibleBuyBoxProps {
    showRadio?: boolean;
}

export const CollapsibleBuyBox = ({showRadio}: CollapsibleBuyBoxProps) => {
    const {gettext} = useI18n();
    const course = useClpCourse();
    const {selectedPurchaseOption, setSelectedPurchaseOption, isReloadingPrice} =
        useClpPurchaseContext();
    const coursePrice = useCoursePrice(Number(course.id));
    const isMobile = useMatchMedia('sm-max');
    const isPersonalPlanPrimaryPurchaseOptions =
        selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION;

    const {discountPrice, listPrice, price} = coursePrice || {};
    const isPriceLoaded = coursePrice?.status === 'loaded' && !isReloadingPrice;

    // Determine if there's a discount - matches the logic from the older implementation
    const showDiscountPrice =
        isPriceLoaded && discountPrice && listPrice && discountPrice.amount !== listPrice.amount;

    const title = (
        <>
            {isReloadingPrice ? (
                <>
                    <span className={classNames('ud-text-md')}>
                        {gettext('Buy individual course')}
                    </span>
                    <TextSkeleton
                        lineCountPerParagraph={0}
                        className={styles['price-skeleton']}
                        withTitle={true}
                    />
                </>
            ) : isPriceLoaded ? (
                <PriceEventTrackingProvider
                    priceEventTrackingContext={{
                        priceType: PURCHASE_PRICE_TYPES.individual_buyable,
                        buyableType: PRICE_EVENTING_BUYABLE_TYPES.COURSE,
                        buyableId: Number(course.id),
                        buyableTrackingId: course.frontendTrackingId,
                    }}
                >
                    <span className={classNames('ud-text-md')}>
                        {gettext('Buy individual course')}
                    </span>
                    <AsyncStaticPriceText
                        showDiscountDuration={true}
                        courseId={Number(course.id)}
                        listPriceString={listPrice?.formattedPrice}
                        discountPriceString={price?.formattedPrice}
                        discountPriceClassName={classNames(
                            'ud-heading-xl',
                            styles['discount-price'],
                        )}
                        discountDeadlineTextClassName={classNames(styles['discount-deadline'])}
                        percentDiscount={coursePrice?.discountPercentForDisplay}
                        percentDiscountClassName={classNames(
                            'ud-text-md',
                            styles['percent-discount'],
                        )}
                        listPriceClassName={classNames(
                            showDiscountPrice ? 'ud-text-md' : 'ud-heading-xl',
                            styles['list-price'],
                        )}
                        showPercentDiscount={showDiscountPrice}
                        className={classNames({
                            [styles['price-text--with-discount']]: showDiscountPrice,
                        })}
                        useCompactDiscountDeadline={true}
                    />
                </PriceEventTrackingProvider>
            ) : null}
        </>
    );

    const content = (
        <>
            <BlockList padding="tight" size="small">
                <BlockList.Item icon={<Icon label="" size="xsmall" glyph="deal" />}>
                    <span className="ud-text-sm">{gettext('30-day money-back guarantee')}</span>
                </BlockList.Item>
                <BlockList.Item icon={<Icon label="" size="xsmall" glyph="lifetime" />}>
                    <span className="ud-text-sm">{gettext('Full lifetime access')}</span>
                </BlockList.Item>
            </BlockList>
            <div
                className={classNames(
                    styles['buttons-container'],
                    styles['buttons-container-collapsible'],
                )}
            >
                <div className={styles['horizontal-buttons']}>
                    <AddToCartButton
                        buyables={[
                            {
                                id: Number(course.id),
                                buyableObjectType: 'course',
                                frontendTrackingId: course.frontendTrackingId,
                            },
                        ]}
                        buttonStyleProps={{
                            size: 'large',
                            udStyle: 'primary',
                        }}
                        addToCartSuccessModal={
                            !isMobile ? <ClpCartSuccessModal course={course} /> : undefined
                        }
                        forceRedirectToCart={!!isMobile}
                    />
                    <WishlistButton courseId={Number(course.id)} />
                </div>
                <TransactionalCTAButton udStyle="secondary" />
            </div>
        </>
    );

    return (
        <CollapsiblePurchaseSection
            showRadio={showRadio}
            title={title}
            defaultExpanded={!isPersonalPlanPrimaryPurchaseOptions}
            onExpand={() => setSelectedPurchaseOption(CLPPurchaseOption.TRANSACTIONAL)}
        >
            {content}
        </CollapsiblePurchaseSection>
    );
};
